/**
 * Backend-voice selection state (#93, D-SEAM / D-CONF).
 *
 * Holds two pieces of process-wide state the voice adapters need:
 *
 *   1. Whether the deployment advertised backend neural TTS / streaming STT
 *      (`GET /api/config.voice`, threaded through `config-api.ts`). The speech
 *      factories (`getSpeechSynthesizer` / `getSpeechRecognizer`) consult this to
 *      pick the Polly/Transcribe adapters over the browser `WebSpeech*` ones.
 *   2. The current game **ticket** the adapters attach for auth on the
 *      `/api/voice/tts` and `/api/voice/stt` routes (published by the WS-connect
 *      path in `use-game.ts`).
 *
 * This module deliberately imports NONE of the adapter modules (speech-synth /
 * speech-recognition / polly-synth / transcribe-recognizer). The factories depend on
 * voice-config, never the reverse — so there is no import cycle. To let
 * `configureVoiceBackend` rebuild the factory singletons with the right adapter, the
 * factory modules REGISTER a reset callback here at load time; `configureVoiceBackend`
 * just invokes them.
 */

let ttsEnabled = false;
let sttEnabled = false;
let voiceTicket: string | null = null;

type ResetFn = () => void;
const resetCallbacks = new Set<ResetFn>();

/**
 * The speech factories register a callback that nulls their cached singleton, so a
 * config change re-selects the correct adapter on the next `get*` call. Kept as a
 * registration (rather than voice-config importing the adapters) to avoid a cycle.
 */
export function registerVoiceSingletonReset(reset: ResetFn): void {
  resetCallbacks.add(reset);
}

function resetSingletons(): void {
  for (const reset of resetCallbacks) reset();
}

/**
 * Record backend-voice availability from `GET /api/config.voice` and reset the
 * factory singletons so the next `getSpeechSynthesizer` / `getSpeechRecognizer` call
 * rebuilds with the newly-selected adapter. Called once from `main.tsx` after the
 * runtime config resolves.
 */
export function configureVoiceBackend(
  voice: { tts: boolean; stt: boolean } | null,
): void {
  ttsEnabled = voice?.tts ?? false;
  sttEnabled = voice?.stt ?? false;
  resetSingletons();
}

/** True iff backend neural TTS was advertised for this deployment. */
export function isBackendTtsEnabled(): boolean {
  return ttsEnabled;
}

/** True iff backend streaming STT was advertised for this deployment. */
export function isBackendSttEnabled(): boolean {
  return sttEnabled;
}

/** Publish the current game ticket for the voice adapters (or clear it with null). */
export function setVoiceTicket(ticket: string | null): void {
  voiceTicket = ticket;
}

/** The current game ticket the voice adapters attach for auth, or null. */
export function getVoiceTicket(): string | null {
  return voiceTicket;
}

/** Test-only: clear all backend-voice state + ticket and reset the singletons. */
export function resetVoiceConfigForTest(): void {
  ttsEnabled = false;
  sttEnabled = false;
  voiceTicket = null;
  resetSingletons();
}
